/**
 * Device Profiles — Option B+ Ultra
 * Per user spec: Vendor, Model, Firmware, ASTM/HL7/IEEE11073, Driver,
 * Connexion TCP/Serial, Version mapping, Last heartbeat,
 * Actions: Edit, Enable/Disable, Maintenance, Ping, Test ASTM, Logs
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Cpu, Search, Plus, Edit, Power, Wrench, Activity, FileText,
  CheckCircle2, XCircle, AlertCircle, Loader2, History, Network, ArrowRight,
} from "lucide-react";
import { getMockProfiles, STATUS_TONE, STATUS_LABEL, TRANSPORT_LABEL, type DeviceProfile, type DeviceStatus } from "@/lib/device/profiles";

export const Route = createFileRoute("/integration/device-profiles")({
  component: DeviceProfiles,
});

function DeviceProfiles() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState<DeviceStatus | "ALL">("ALL");
  const [selected, setSelected] = useState<DeviceProfile | null>(null);
  const [pingResult, setPingResult] = useState<{ ok: boolean; latency: number } | null>(null);
  const [pinging, setPinging] = useState(false);

  // For now: use mock data + local state for mutations
  // In production: useQuery(["devices"], () => apiFetch("/api/device/profiles", ...))
  const profilesQ = useQuery({
    queryKey: ["device-profiles"],
    queryFn: async () => {
      // Simulated latency
      await new Promise((r) => setTimeout(r, 200));
      return getMockProfiles();
    },
  });

  const profiles = profilesQ.data ?? [];

  const filtered = useMemo(() => {
    return profiles.filter((p) => {
      if (filterStatus !== "ALL" && p.status !== filterStatus) return false;
      if (!search) return true;
      const q = search.toLowerCase();
      return (
        p.vendor.toLowerCase().includes(q) ||
        p.model.toLowerCase().includes(q) ||
        p.driver.toLowerCase().includes(q) ||
        p.host.includes(q)
      );
    });
  }, [profiles, search, filterStatus]);

  const counts = useMemo(() => {
    const c: Record<DeviceStatus | "ALL", number> = { ALL: profiles.length, ACTIVE: 0, OFFLINE: 0, MAINTENANCE: 0, ERROR: 0 };
    for (const p of profiles) c[p.status]++;
    return c;
  }, [profiles]);

  function handlePing(profile: DeviceProfile) {
    setPinging(true);
    setPingResult(null);
    setTimeout(() => {
      const ok = profile.status !== "OFFLINE";
      setPingResult({ ok, latency: 12 + Math.floor(Math.random() * 80) });
      setPinging(false);
    }, 800);
  }

  function handleToggleEnabled(profile: DeviceProfile) {
    qc.setQueryData<DeviceProfile[]>(["device-profiles"], (old) =>
      (old ?? []).map((p) => p.id === profile.id ? { ...p, enabled: !p.enabled, status: !p.enabled ? "ACTIVE" : "OFFLINE" } : p)
    );
  }

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Cpu className="h-5 w-5" /> Profils dispositifs
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Vendor · Model · Driver · Transport HL7/ASTM/IEEE 11073/DICOM · IEC 62304
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm"><Plus className="h-3.5 w-3.5 mr-1" /> Nouveau profil</Button>
          <Button size="sm"><Network className="h-3.5 w-3.5 mr-1" /> Auto-détection</Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Rechercher (vendor, model, driver, IP)..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-9"
          />
        </div>
        <Tabs value={filterStatus} onValueChange={(v) => setFilterStatus(v as any)}>
          <TabsList className="h-9">
            <TabsTrigger value="ALL" className="text-xs">Tous ({counts.ALL})</TabsTrigger>
            <TabsTrigger value="ACTIVE" className="text-xs">Actifs ({counts.ACTIVE})</TabsTrigger>
            <TabsTrigger value="OFFLINE" className="text-xs">Offline ({counts.OFFLINE})</TabsTrigger>
            <TabsTrigger value="MAINTENANCE" className="text-xs">Maintenance ({counts.MAINTENANCE})</TabsTrigger>
            <TabsTrigger value="ERROR" className="text-xs">Erreurs ({counts.ERROR})</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Device list */}
      <div className="grid gap-2">
        {profilesQ.isLoading ? (
          Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-20 w-full" />)
        ) : filtered.length === 0 ? (
          <div className="rounded-lg border border-dashed border-white/10 p-8 text-center text-sm text-muted-foreground">
            Aucun dispositif ne correspond
          </div>
        ) : (
          filtered.map((p) => (
            <Card key={p.id} className="overflow-hidden">
              <CardContent className="p-0">
                <div className="flex items-center gap-4 px-4 py-3">
                  {/* Status indicator */}
                  <div className={`h-2 w-2 rounded-full ${
                    p.status === "ACTIVE" ? "bg-emerald-400" :
                    p.status === "OFFLINE" ? "bg-slate-500" :
                    p.status === "MAINTENANCE" ? "bg-amber-400" : "bg-red-500"
                  }`} />

                  {/* Vendor + model */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium truncate">{p.vendor} {p.model}</span>
                      <Badge variant={STATUS_TONE[p.status]} className="text-[10px]">{STATUS_LABEL[p.status]}</Badge>
                      {p.iec11073Conformant && (
                        <Badge variant="outline" className="text-[10px]">IEEE 11073</Badge>
                      )}
                      <Badge variant="outline" className="text-[10px]">IEC 62304 {p.iec62304Class}</Badge>
                    </div>
                    <div className="mt-1 flex items-center gap-3 text-xs text-muted-foreground">
                      <span className="font-mono">{p.host}:{p.port}</span>
                      <span>·</span>
                      <span>{TRANSPORT_LABEL[p.transport]}</span>
                      <span>·</span>
                      <span>FW {p.firmware}</span>
                      <span>·</span>
                      <span>Mapping {p.mappingVersion}</span>
                      <span>·</span>
                      <span>Dernier heartbeat: {timeAgo(p.lastHeartbeat)}</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" title="Éditer" onClick={() => setSelected(p)}>
                      <Edit className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      title={p.enabled ? "Désactiver" : "Activer"}
                      onClick={() => handleToggleEnabled(p)}
                    >
                      <Power className={`h-3.5 w-3.5 ${p.enabled ? "text-emerald-400" : "text-slate-500"}`} />
                    </Button>
                    <Button variant="ghost" size="icon" title="Maintenance" onClick={() => setSelected(p)}>
                      <Wrench className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" title="Logs" onClick={() => setSelected(p)}>
                      <History className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { setSelected(p); handlePing(p); }}
                      disabled={pinging && selected?.id === p.id}
                    >
                      {pinging && selected?.id === p.id ? (
                        <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                      ) : (
                        <Activity className="h-3.5 w-3.5 mr-1" />
                      )}
                      Ping
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => { setSelected(o ? selected : null); setPingResult(null); }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Cpu className="h-4 w-4" />
                  {selected.vendor} {selected.model}
                  <Badge variant={STATUS_TONE[selected.status]} className="ml-2">{STATUS_LABEL[selected.status]}</Badge>
                </DialogTitle>
                <DialogDescription>
                  Driver: <code className="text-xs">{selected.driver}</code>
                </DialogDescription>
              </DialogHeader>

              <Tabs defaultValue="info" className="mt-2">
                <TabsList>
                  <TabsTrigger value="info">Informations</TabsTrigger>
                  <TabsTrigger value="connexion">Connexion</TabsTrigger>
                  <TabsTrigger value="logs">Historique ({selected.recentLogs.length})</TabsTrigger>
                </TabsList>

                <TabsContent value="info" className="space-y-3">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <Field label="Vendor" value={selected.vendor} />
                    <Field label="Model" value={selected.model} />
                    <Field label="Firmware" value={selected.firmware} />
                    <Field label="Mapping version" value={selected.mappingVersion} />
                    <Field label="Transport" value={TRANSPORT_LABEL[selected.transport]} />
                    <Field label="IEC 62304 class" value={selected.iec62304Class} />
                    <Field label="IEEE 11073" value={selected.iec11073Conformant ? "Conforme" : "—"} />
                    <Field label="Statut" value={STATUS_LABEL[selected.status]} />
                  </div>
                </TabsContent>

                <TabsContent value="connexion" className="space-y-3">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <Field label="Host" value={selected.host} />
                    <Field label="Port" value={String(selected.port)} />
                    <Field label="Dernier heartbeat" value={new Date(selected.lastHeartbeat).toLocaleString("fr-FR")} />
                    <Field label="Actif" value={selected.enabled ? "Oui" : "Non"} />
                  </div>

                  <div className="rounded-lg border border-white/10 p-3 mt-3">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-medium">Test de connectivité</span>
                      <Button size="sm" variant="outline" onClick={() => handlePing(selected)} disabled={pinging}>
                        {pinging ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Activity className="h-3 w-3 mr-1" />}
                        Ping
                      </Button>
                      <Button size="sm" variant="outline">
                        <FileText className="h-3 w-3 mr-1" /> Test ASTM
                      </Button>
                    </div>
                    {pingResult && (
                      <div className={`flex items-center gap-2 text-sm ${pingResult.ok ? "text-emerald-400" : "text-red-400"}`}>
                        {pingResult.ok ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                        {pingResult.ok ? `OK (${pingResult.latency} ms)` : "Échec"}
                      </div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="logs" className="space-y-1">
                  {selected.recentLogs.map((log) => (
                    <div key={log.id} className="flex items-center gap-2 rounded-md border border-white/5 bg-white/[0.02] p-2 text-xs">
                      <span className="text-muted-foreground font-mono w-32 shrink-0">
                        {new Date(log.timestamp).toLocaleString("fr-FR")}
                      </span>
                      {log.result === "SUCCESS" ? (
                        <CheckCircle2 className="h-3 w-3 text-emerald-400" />
                      ) : log.result === "WARNING" ? (
                        <AlertCircle className="h-3 w-3 text-amber-400" />
                      ) : (
                        <XCircle className="h-3 w-3 text-red-400" />
                      )}
                      <span className="font-medium">{log.observation}</span>
                      <span className="text-muted-foreground">·</span>
                      <span className="text-muted-foreground">Patient {log.patientMrn}</span>
                      <span className="ml-auto text-muted-foreground">{log.durationMs} ms</span>
                    </div>
                  ))}
                </TabsContent>
              </Tabs>

              <DialogFooter>
                <Button variant="outline" onClick={() => setSelected(null)}>Fermer</Button>
                <Button><Edit className="h-3.5 w-3.5 mr-1" /> Éditer le profil</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-sm">{value}</div>
    </div>
  );
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}j`;
}
